import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import ConfirmModal from './ConfirmModal'
import {
  loadKanbanConfigRemoto,
  saveKanbanConfigRemoto,
  normalizeKanbanConfig
} from '../services/leadsKanbanConfig'
import {
  mergeColunas,
  getColunasVisiveis,
  findColunaPorId,
  criarColunaCustom,
  tituloParaStatusId
} from '../constants/leadsKanbanColunas'
import { USER_ROLES } from '../config/security'
import './Leads.css'

const ROLES_GERENCIAM_KANBAN_LEADS = [USER_ROLES.ADMINISTRADOR, USER_ROLES.ADMINISTRATIVO]

/**
 * Componente de Gestão de Leads com Kanban
 * 
 * Estrutura Kanban com 6 colunas:
 * - Novo Lead: Leads recém-cadastrados
 * - Contato Feito: Primeiro contato realizado
 * - Proposta Enviada: Orçamento enviado
 * - Negociação: Cliente interessado, negociando
 * - Fechado (Ganhou): Venda concluída
 * - Fechado (Perdido): Lead perdido
 */
const Leads = ({ onBack, userRole, userEmpresa, userId, onIniciarEsteira }) => {
  const podeGerenciarColunasKanban = ROLES_GERENCIAM_KANBAN_LEADS.includes(userRole)
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingLead, setEditingLead] = useState(null)
  const [draggedLead, setDraggedLead] = useState(null)
  const [dragOverColumn, setDragOverColumn] = useState(null)
  const [dragOverCard, setDragOverCard] = useState(null)
  const [showConfetti, setShowConfetti] = useState(false)
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [leadToMove, setLeadToMove] = useState(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [leadHistory, setLeadHistory] = useState([])
  const [selectedLeadForHistory, setSelectedLeadForHistory] = useState(null)
  const [showFiltros, setShowFiltros] = useState(false)
  const [responsaveisList, setResponsaveisList] = useState([])
  const [filtros, setFiltros] = useState({
    responsavel: '',
    dataInicio: '',
    dataFim: '',
    nome: '',
    valorOperador: '',
    valor: ''
  })
  const filtrosRef = useRef(filtros)
  useEffect(() => { filtrosRef.current = filtros }, [filtros])

  const [kanbanConfig, setKanbanConfig] = useState({ customColunas: [], ocultas: [] })
  const [showNovaColunaModal, setShowNovaColunaModal] = useState(false)
  const [showColunasOcultasModal, setShowColunasOcultasModal] = useState(false)
  const [novaColunaForm, setNovaColunaForm] = useState({ titulo: '', descricao: '', emoji: '📌' })

  const colunasTodas = useMemo(
    () => mergeColunas(kanbanConfig.customColunas),
    [kanbanConfig.customColunas]
  )
  const colunas = useMemo(
    () => getColunasVisiveis(colunasTodas, kanbanConfig.ocultas),
    [colunasTodas, kanbanConfig.ocultas]
  )
  const colunasOcultasLista = useMemo(
    () => colunasTodas.filter((c) => kanbanConfig.ocultas.includes(c.id)),
    [colunasTodas, kanbanConfig.ocultas]
  )

  const empresaIdKanban = userEmpresa?.id || userEmpresa

  const aplicarConfigKanban = useCallback((cfg) => {
    setKanbanConfig({
      customColunas: cfg?.customColunas || [],
      ocultas: cfg?.ocultas || []
    })
  }, [])

  const recarregarConfigKanban = useCallback(async () => {
    const cfg = await loadKanbanConfigRemoto(empresaIdKanban)
    aplicarConfigKanban(cfg)
    return cfg
  }, [empresaIdKanban, aplicarConfigKanban])

  useEffect(() => {
    recarregarConfigKanban()
  }, [recarregarConfigKanban])

  /** Atualização em tempo real: admin oculta coluna → demais usuários veem só as visíveis. */
  useEffect(() => {
    if (!empresaIdKanban) return undefined

    const channel = supabase
      .channel(`leads-kanban-config-${empresaIdKanban}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads_kanban_config',
          filter: `empresa_id=eq.${empresaIdKanban}`
        },
        (payload) => {
          const row = payload.new || payload.old
          if (row && (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT')) {
            aplicarConfigKanban(normalizeKanbanConfig(row))
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [empresaIdKanban, aplicarConfigKanban])

  const triggerConfetti = () => {
    setShowConfetti(true)
    setTimeout(() => setShowConfetti(false), 2000)
  }

  const handleMoveLead = async (lead, novoStatus) => {
    if (lead.status === novoStatus) {
      setShowMoveModal(false)
      setLeadToMove(null)
      return
    }

    try {
      setLoading(true)
      const statusAnterior = lead.status
      
      const { error } = await supabase
        .from('leads')
        .update({ 
          status: novoStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', lead.id)

      if (error) throw error

      // Registrar histórico da movimentação
      await registrarHistorico(lead.id, statusAnterior, novoStatus, `Movido de "${findColunaPorId(colunasTodas, statusAnterior).titulo}" para "${findColunaPorId(colunasTodas, novoStatus).titulo}"`)

      // Adicionar animação de sucesso
      const cardElement = document.querySelector(`[data-lead-id="${lead.id}"]`)
      if (cardElement) {
        cardElement.classList.add('drop-success')
        setTimeout(() => {
          cardElement.classList.remove('drop-success')
        }, 600)
      }

      // Trigger confetti para status de sucesso
      if (novoStatus === 'fechado_ganhou') {
        triggerConfetti()
      }

      showMessage('Status do lead atualizado!', 'success')
      loadLeads()
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      showMessage('Erro ao atualizar status do lead', 'error')
    } finally {
      setLoading(false)
      setShowMoveModal(false)
      setLeadToMove(null)
    }
  }

  const openMoveModal = (lead) => {
    setLeadToMove(lead)
    setShowMoveModal(true)
  }

  const registrarHistorico = async (leadId, statusAnterior, statusNovo, observacoes = '') => {
    try {
      // Descrições específicas para cada tipo de movimentação
      let descricaoObservacao = observacoes
      
      if (!observacoes) {
        if (statusNovo === 'parado') {
          descricaoObservacao = 'Lead movido para "Parado" - Aguardando condições externas (financiamento, reforma futura, etc.)'
        } else if (statusAnterior === 'parado') {
          descricaoObservacao = 'Lead reativado do status "Parado"'
        } else {
          descricaoObservacao = 'Movimentação via sistema'
        }
      }

      const { error } = await supabase
        .from('leads_historico')
        .insert([{
          lead_id: leadId,
          status_anterior: statusAnterior,
          status_novo: statusNovo,
          usuario_id: userId,
          observacoes: descricaoObservacao
        }])

      if (error) throw error
    } catch (error) {
      console.error('Erro ao registrar histórico:', error)
    }
  }

  const carregarHistorico = async (leadId) => {
    try {
      // Primeiro, buscar o histórico
      const { data: historicoData, error: historicoError } = await supabase
        .from('leads_historico')
        .select('*')
        .eq('lead_id', leadId)
        .order('data_movimentacao', { ascending: false })

      if (historicoError) throw historicoError

      // Buscar nomes dos usuários
      const userIds = [...new Set(historicoData?.map(item => item.usuario_id) || [])]
      const { data: usuariosData, error: usuariosError } = await supabase
        .from('usuarios_sistema')
        .select('id, nome')
        .in('id', userIds)

      if (usuariosError) throw usuariosError

      // Combinar os dados
      const historicoComUsuarios = historicoData?.map(item => ({
        ...item,
        usuarios: usuariosData?.find(user => user.id === item.usuario_id)
      })) || []

      setLeadHistory(historicoComUsuarios)
    } catch (error) {
      console.error('Erro ao carregar histórico:', error)
      showMessage('Erro ao carregar histórico', 'error')
    }
  }

  const abrirHistorico = async (lead) => {
    setSelectedLeadForHistory(lead)
    await carregarHistorico(lead.id)
    setShowHistoryModal(true)
  }

  const handleIniciarEsteira = (lead) => {
    if (!onIniciarEsteira) {
      showMessage('Função de iniciar esteira não configurada', 'error')
      return
    }

    // Preparar dados do cliente para iniciar esteira
    const dadosCliente = {
      nome: lead.nome,
      telefone: lead.telefone,
      email: lead.email,
      origem: 'Lead convertido',
      observacoes: `Lead convertido de: ${lead.origem}\nObservações: ${lead.observacoes || 'Nenhuma observação'}`,
      valor_interesse: lead.valor_interesse,
      data_contato: lead.data_contato,
      empresa_id: lead.empresa_id,
      usuario_id: userId
    }

    // Chamar função para iniciar esteira
    onIniciarEsteira(dadosCliente)
  }

  const [confirmModal, setConfirmModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null, 
    type: 'warning' 
  })
  const [messageModal, setMessageModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    type: 'success' 
  })

  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    origem: 'formulario', // formulario, whatsapp, indicacao, outros
    observacoes: '',
    valor_interesse: '',
    status: 'novo_lead',
    data_contato: new Date().toISOString().split('T')[0], // Data de hoje por padrão
    proximo_contato: '',
    responsavel: '',
    empresa_id: userEmpresa?.id || userEmpresa || ''
  })

  const origens = [
    { value: 'formulario', label: 'Formulário Website' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'indicacao', label: 'Indicação' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'google', label: 'Google Ads' },
    { value: 'telefone', label: 'Telefone' },
    { value: 'pap', label: 'PAP (Venda Porta a Porta)' },
    { value: 'lead_proprio', label: 'Lead Próprio' },
    { value: 'painel_dados', label: 'Painel de Dados' }
  ]

  const loadResponsaveis = useCallback(async () => {
    try {
      let query = supabase.from('usuarios_sistema').select('id, nome').eq('status', 'ativo').order('nome')
      // Sempre filtrar pela empresa do usuário logado
      const empresaId = userEmpresa?.id || userEmpresa
      if (empresaId) {
        query = query.eq('empresa_id', empresaId)
      }
      const { data, error } = await query
      if (error) throw error
      setResponsaveisList(data || [])
    } catch (err) {
      console.error('Erro ao carregar responsáveis:', err)
    }
  }, [userEmpresa])

  const loadLeads = useCallback(async (filtrosOverride) => {
    const f = filtrosOverride !== undefined ? filtrosOverride : filtrosRef.current
    try {
      setLoading(true)
      
      let query = supabase.from('leads').select('*')
      
      if (userRole === 'administrador') {
        query = query.order('created_at', { ascending: false })
      } else if (userRole === 'gerente' || userRole === 'administrativo' || userRole === 'sdr') {
        const empresaId = userEmpresa?.id || userEmpresa
        if (!empresaId) {
          setLeads([])
          setLoading(false)
          return
        }
        query = query.eq('empresa_id', empresaId).order('created_at', { ascending: false })
      } else if (userRole === 'especialista') {
        const empresaId = userEmpresa?.id || userEmpresa
        if (!empresaId || !userId) {
          setLeads([])
          setLoading(false)
          return
        }
        query = query
          .eq('empresa_id', empresaId)
          .eq('responsavel', userId)
          .order('created_at', { ascending: false })
      } else {
        setLeads([])
        setLoading(false)
        return
      }

      if (f.responsavel) query = query.eq('responsavel', f.responsavel)
      if (f.dataInicio) query = query.gte('data_contato', f.dataInicio)
      if (f.dataFim) query = query.lte('data_contato', f.dataFim)
      if (f.nome && f.nome.trim()) query = query.ilike('nome', `%${f.nome.trim()}%`)
      if (f.valorOperador && f.valor !== '') {
        const valorNum = parseFloat(String(f.valor).replace(/[^\d,.-]/g, '').replace(',', '.'))
        if (!isNaN(valorNum)) {
          if (f.valorOperador === '>') query = query.gt('valor_interesse', valorNum)
          else if (f.valorOperador === '<') query = query.lt('valor_interesse', valorNum)
          else if (f.valorOperador === '=') query = query.eq('valor_interesse', valorNum)
        }
      }

      const { data, error } = await query
      if (error) throw error
      setLeads(data || [])
    } catch (error) {
      console.error('Erro ao carregar leads:', error)
      showMessage('Erro ao carregar leads', 'error')
    } finally {
      setLoading(false)
    }
  }, [userRole, userEmpresa, userId])

  useEffect(() => {
    if (userRole && (userEmpresa || userRole === 'administrador')) {
      loadLeads()
    }
  }, [userRole, userEmpresa, userId, loadLeads])

  useEffect(() => {
    if (userRole && (userEmpresa || userRole === 'administrador')) {
      loadResponsaveis()
    }
  }, [userRole, userEmpresa, loadResponsaveis])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      
      const empresaId = userEmpresa?.id || userEmpresa
      
      // Construir objeto leadData apenas com os campos válidos da tabela leads
      const leadData = {
        nome: formData.nome || '',
        telefone: formData.telefone || '',
        origem: formData.origem || 'formulario',
        observacoes: formData.observacoes || '',
        valor_interesse: formData.valor_interesse ? parseFloat(formData.valor_interesse.replace(/[^\d,]/g, '').replace(',', '.')) : null,
        status: formData.status || 'novo_lead',
        data_contato: formData.data_contato || new Date().toISOString().split('T')[0],
        proximo_contato: formData.proximo_contato || null,
        empresa_id: empresaId
      }
      
      // Responsável: ao criar usa o selecionado ou o usuário logado; ao editar usa o selecionado no formulário
      if (editingLead) {
        leadData.responsavel = formData.responsavel || null
      } else if (userId) {
        leadData.responsavel = formData.responsavel || userId
      }

      // Verificar se especialista está tentando editar lead de outro usuário
      if (userRole === 'especialista' && editingLead && editingLead.responsavel !== userId) {
        showMessage('Você só pode editar leads criados por você', 'error')
        setLoading(false)
        return
      }

      if (editingLead) {
        const { error } = await supabase
          .from('leads')
          .update(leadData)
          .eq('id', editingLead.id)

        if (error) throw error
        showMessage('Lead atualizado com sucesso!', 'success')
      } else {
        const { error } = await supabase
          .from('leads')
          .insert([leadData])

        if (error) throw error
        showMessage('Lead cadastrado com sucesso!', 'success')
      }

      setShowForm(false)
      setEditingLead(null)
      resetForm()
      loadLeads()
    } catch (error) {
      console.error('Erro ao salvar lead:', error)
      showMessage('Erro ao salvar lead', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (lead) => {
    setEditingLead(lead)
    setFormData({
      nome: lead.nome || '',
      telefone: lead.telefone || '',
      origem: lead.origem || 'formulario',
      observacoes: lead.observacoes || '',
      valor_interesse: lead.valor_interesse ? lead.valor_interesse.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) : '',
      status: lead.status || 'novo_lead',
      data_contato: lead.data_contato || new Date().toISOString().split('T')[0],
      proximo_contato: lead.proximo_contato || '',
      responsavel: lead.responsavel || ''
    })
    setShowForm(true)
  }

  const handleDelete = (lead) => {
    // Verificar se especialista está tentando deletar lead de outro usuário
    if (userRole === 'especialista' && lead.responsavel !== userId) {
      showMessage('Você só pode excluir leads criados por você', 'error')
      return
    }

    setConfirmModal({
      show: true,
      title: 'Confirmar Exclusão',
      message: `Tem certeza que deseja excluir o lead "${lead.nome}"?`,
      type: 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('leads')
            .delete()
            .eq('id', lead.id)

          if (error) throw error
          showMessage('Lead excluído com sucesso!', 'success')
          loadLeads()
        } catch (error) {
          console.error('Erro ao excluir lead:', error)
          showMessage('Erro ao excluir lead', 'error')
        }
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'warning' })
      }
    })
  }

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.outerHTML)
    
    // Adicionar classe de dragging ao elemento
    setTimeout(() => {
      e.target.classList.add('dragging')
    }, 0)
  }

  const handleDragEnd = (e) => {
    // Remover classe de dragging
    e.target.classList.remove('dragging')
    setDraggedLead(null)
    setDragOverColumn(null)
    setDragOverCard(null)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e, columnId, cardId = null) => {
    e.preventDefault()
    setDragOverColumn(columnId)
    setDragOverCard(cardId)
  }

  const handleDragLeave = (e) => {
    // Só limpa se realmente saiu da área de drop
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null)
      setDragOverCard(null)
    }
  }

  const handleDrop = async (e, novoStatus) => {
    e.preventDefault()
    
    if (!draggedLead || draggedLead.status === novoStatus) {
      setDraggedLead(null)
      setDragOverColumn(null)
      setDragOverCard(null)
      return
    }

    try {
      const statusAnterior = draggedLead.status
      
      const { error } = await supabase
        .from('leads')
        .update({ 
          status: novoStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', draggedLead.id)

      if (error) throw error

      // Registrar histórico da movimentação
      await registrarHistorico(draggedLead.id, statusAnterior, novoStatus, `Movido via drag and drop de "${findColunaPorId(colunasTodas, statusAnterior).titulo}" para "${findColunaPorId(colunasTodas, novoStatus).titulo}"`)

      // Adicionar animação de sucesso ao card
      const cardElement = e.target.closest('.lead-card')
      if (cardElement) {
        cardElement.classList.add('drop-success')
        setTimeout(() => {
          cardElement.classList.remove('drop-success')
        }, 600)
      }

      // Trigger confetti para status de sucesso
      if (novoStatus === 'fechado_ganhou') {
        triggerConfetti()
      }

      showMessage('Status do lead atualizado!', 'success')
      loadLeads()
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      showMessage('Erro ao atualizar status do lead', 'error')
    } finally {
      setDraggedLead(null)
      setDragOverColumn(null)
      setDragOverCard(null)
    }
  }

  const resetForm = () => {
    const empresaId = userEmpresa?.id || userEmpresa
    setFormData({
      nome: '',
      telefone: '',
      origem: 'formulario',
      observacoes: '',
      valor_interesse: '',
      status: 'novo_lead',
      data_contato: new Date().toISOString().split('T')[0], // Data de hoje por padrão
      proximo_contato: '',
      responsavel: '',
      empresa_id: empresaId
    })
  }

  const showMessage = (message, type = 'success') => {
    setMessageModal({
      show: true,
      title: type === 'success' ? 'Sucesso' : 'Erro',
      message: message,
      type: type
    })
    setTimeout(() => {
      setMessageModal({ show: false, title: '', message: '', type: 'success' })
    }, 3000)
  }

  const salvarConfigKanbanEmpresa = async (nextConfig) => {
    const res = await saveKanbanConfigRemoto(empresaIdKanban, nextConfig)
    if (!res.ok) {
      showMessage(
        'Não foi possível salvar para todos os usuários: ' + (res.error || 'Verifique a tabela leads_kanban_config no Supabase.'),
        'error'
      )
      await recarregarConfigKanban()
      return false
    }
    aplicarConfigKanban(res.config || nextConfig)
    return true
  }

  const ocultarColunaKanban = async (colunaId) => {
    if (!podeGerenciarColunasKanban) return
    const next = {
      ...kanbanConfig,
      ocultas: kanbanConfig.ocultas.includes(colunaId)
        ? kanbanConfig.ocultas
        : [...kanbanConfig.ocultas, colunaId]
    }
    aplicarConfigKanban(next)
    const ok = await salvarConfigKanbanEmpresa(next)
    if (ok) {
      showMessage('Coluna ocultada para todos os usuários da empresa.', 'success')
    }
  }

  const restaurarColunaKanban = async (colunaId) => {
    if (!podeGerenciarColunasKanban) return
    const next = {
      ...kanbanConfig,
      ocultas: kanbanConfig.ocultas.filter((id) => id !== colunaId)
    }
    aplicarConfigKanban(next)
    const ok = await salvarConfigKanbanEmpresa(next)
    if (ok) showMessage('Coluna exibida novamente para todos.', 'success')
  }

  const handleCriarColunaKanban = async (e) => {
    e.preventDefault()
    if (!podeGerenciarColunasKanban) return
    const titulo = novaColunaForm.titulo.trim()
    if (!titulo) {
      showMessage('Informe o nome da coluna.', 'error')
      return
    }
    const statusId = tituloParaStatusId(titulo)
    if (colunasTodas.some((c) => c.id === statusId || c.titulo.toLowerCase() === titulo.toLowerCase())) {
      showMessage('Já existe uma coluna com este nome ou status.', 'error')
      return
    }
    const nova = criarColunaCustom(titulo, novaColunaForm.descricao, novaColunaForm.emoji, colunasTodas)
    const next = {
      ...kanbanConfig,
      customColunas: [...kanbanConfig.customColunas, nova]
    }
    aplicarConfigKanban(next)
    const ok = await salvarConfigKanbanEmpresa(next)
    if (ok) {
      setNovaColunaForm({ titulo: '', descricao: '', emoji: '📌' })
      setShowNovaColunaModal(false)
      showMessage(`Coluna criada para todos. Status em leads: "${nova.id}"`, 'success')
    }
  }

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getLeadsByStatus = (status) => {
    return leads.filter(lead => lead.status === status)
  }

  return (
    <div className="leads-container">
      <div className="leads-header">
        <button className="btn-back btn-back-discreet" onClick={onBack} title="Voltar">
          ←
        </button>
        <button
          type="button"
          className="btn-toggle-filtros"
          onClick={() => setShowFiltros(s => !s)}
          aria-expanded={showFiltros}
        >
          {showFiltros ? '▲ Ocultar filtros' : '▼ Mostrar filtros'}
        </button>
        {podeGerenciarColunasKanban && (
          <div className="leads-admin-kanban-actions">
            <button
              type="button"
              className="btn-nova-coluna-kanban"
              onClick={() => setShowNovaColunaModal(true)}
              title="Criar nova coluna no Kanban"
            >
              + Nova coluna
            </button>
            {colunasOcultasLista.length > 0 && (
              <button
                type="button"
                className="btn-colunas-ocultas"
                onClick={() => setShowColunasOcultasModal(true)}
                title="Exibir colunas ocultas"
              >
                Colunas ocultas ({colunasOcultasLista.length})
              </button>
            )}
          </div>
        )}
      </div>

      <div className="leads-filtros-bar">
        {showFiltros && (
          <div className="leads-filtros-form">
            <div className="filtro-group">
              <label>Responsável</label>
              <select
                value={filtros.responsavel}
                onChange={(e) => setFiltros(f => ({ ...f, responsavel: e.target.value }))}
              >
                <option value="">Todos</option>
                {responsaveisList.map(u => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
            <div className="filtro-group">
              <label>Data início</label>
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros(f => ({ ...f, dataInicio: e.target.value }))}
              />
            </div>
            <div className="filtro-group">
              <label>Data fim</label>
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros(f => ({ ...f, dataFim: e.target.value }))}
              />
            </div>
            <div className="filtro-group">
              <label>Nome do lead</label>
              <input
                type="text"
                placeholder="Buscar por nome"
                value={filtros.nome}
                onChange={(e) => setFiltros(f => ({ ...f, nome: e.target.value }))}
              />
            </div>
            <div className="filtro-group filtro-valor">
              <label>Valor conta de energia</label>
              <div className="filtro-valor-inputs">
                <select
                  value={filtros.valorOperador}
                  onChange={(e) => setFiltros(f => ({ ...f, valorOperador: e.target.value }))}
                >
                  <option value="">—</option>
                  <option value=">">Maior que (&gt;)</option>
                  <option value="<">Menor que (&lt;)</option>
                  <option value="=">Igual (=)</option>
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Valor"
                  value={filtros.valor}
                  onChange={(e) => setFiltros(f => ({ ...f, valor: e.target.value }))}
                />
              </div>
            </div>
            <div className="filtro-actions">
              <button type="button" className="btn-filtrar" onClick={() => loadLeads()}>
                Filtrar
              </button>
              <button
                type="button"
                className="btn-limpar-filtros"
                onClick={() => {
                  const vazio = {
                    responsavel: '',
                    dataInicio: '',
                    dataFim: '',
                    nome: '',
                    valorOperador: '',
                    valor: ''
                  }
                  setFiltros(vazio)
                  loadLeads(vazio)
                }}
              >
                Limpar
              </button>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Carregando leads...</p>
        </div>
      )}

      <div className="kanban-board">
        {colunas.map(coluna => (
          <div 
            key={coluna.id}
            className={`kanban-column ${dragOverColumn === coluna.id ? 'drag-over' : ''}`}
            data-status={coluna.id}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, coluna.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, coluna.id)}
          >
            <div className="column-header" style={{ backgroundColor: coluna.corFundo }}>
              {podeGerenciarColunasKanban && (
                <button
                  type="button"
                  className="btn-ocultar-coluna-kanban"
                  onClick={() => ocultarColunaKanban(coluna.id)}
                  title="Ocultar esta coluna do quadro"
                  aria-label={`Ocultar coluna ${coluna.titulo}`}
                >
                  ×
                </button>
              )}
              <div className="column-title">
                <span className="column-emoji">{coluna.emoji}</span>
                <h3>{coluna.titulo}</h3>
              </div>
              <div className="column-count">
                {getLeadsByStatus(coluna.id).length}
              </div>
              <p className="column-description">{coluna.descricao}</p>
            </div>
            
            <div className="column-content">
              {getLeadsByStatus(coluna.id).map((lead, index) => (
                <div
                  key={lead.id}
                  className={`lead-card ${dragOverCard === lead.id ? 'drag-over' : ''}`}
                  data-lead-id={lead.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead)}
                  onDragEnd={handleDragEnd}
                  onDragEnter={(e) => handleDragEnter(e, coluna.id, lead.id)}
                  onDragLeave={handleDragLeave}
                >
                  <div className="lead-header">
                    <div className="lead-header-content">
                      <h4>{lead.nome}</h4>
                    </div>
                  </div>
                  
                  <div className="lead-info">
                    {lead.telefone && (
                      <p><strong>📞</strong> {lead.telefone}</p>
                    )}
                    {lead.email && (
                      <p><strong>📧</strong> {lead.email}</p>
                    )}
                    {lead.empresa && (
                      <p><strong>🏢</strong> {lead.empresa}</p>
                    )}
                    {lead.valor_interesse && (
                      <p><strong>💰</strong> {formatCurrency(lead.valor_interesse)}</p>
                    )}
                  </div>
                  
                  <div className="lead-meta">
                    <span className="origem-badge">
                      {origens.find(o => o.value === lead.origem)?.label || lead.origem}
                    </span>
                    {(lead.data_hora_contato || lead.data_contato) && (
                      <span className="data-info">
                        📅 {formatDate(lead.data_hora_contato || lead.data_contato)}
                      </span>
                    )}
                    {lead.responsavel && (
                      <span className="lead-responsavel" title="Responsável pelo lead">
                        👤 {responsaveisList.find(u => u.id === lead.responsavel)?.nome || '—'}
                      </span>
                    )}
                  </div>
                  
                  {lead.observacoes && (
                    <div className="lead-observacoes">
                      <p>{lead.observacoes}</p>
                    </div>
                  )}
                  
                  <div className="lead-actions">
                    <button 
                      className="btn-move"
                      onClick={() => openMoveModal(lead)}
                      title="Mover Lead"
                    >
                      📋
                    </button>
                    <button 
                      className="btn-history"
                      onClick={() => abrirHistorico(lead)}
                      title="Ver Histórico"
                    >
                      📊
                    </button>
                    {lead.status === 'fechado_ganhou' && (
                      <button 
                        className="btn-start"
                        onClick={() => handleIniciarEsteira(lead)}
                        title="Iniciar esteira do cliente"
                      >
                        ▶️
                      </button>
                    )}
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(lead)}
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(lead)}
                      title="Excluir"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Efeito de Confetti */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe'][Math.floor(Math.random() * 5)]
              }}
            />
          ))}
        </div>
      )}

      {/* Modal de Formulário */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingLead ? 'Editar Lead' : 'Novo Lead'}</h3>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowForm(false)
                  setEditingLead(null)
                  resetForm()
                }}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="lead-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    required
                    placeholder="Nome do lead"
                  />
                </div>
                
                <div className="form-group">
                  <label>Telefone *</label>
                  <input
                    type="tel"
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                    required
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Origem *</label>
                  <select
                    value={formData.origem}
                    onChange={(e) => setFormData({...formData, origem: e.target.value})}
                    required
                  >
                    {origens.map(origem => (
                      <option key={origem.value} value={origem.value}>
                        {origem.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Valor da conta de energia</label>
                  <input
                    type="text"
                    value={formData.valor_interesse}
                    onChange={(e) => setFormData({...formData, valor_interesse: e.target.value})}
                    placeholder="R$ 0,00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Responsável</label>
                  <select
                    value={formData.responsavel}
                    onChange={(e) => setFormData({...formData, responsavel: e.target.value})}
                  >
                    <option value="">Selecione o responsável</option>
                    {responsaveisList.map(u => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Data do Contato</label>
                  <input
                    type="date"
                    value={formData.data_contato}
                    onChange={(e) => setFormData({...formData, data_contato: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Próximo Contato</label>
                  <input
                    type="date"
                    value={formData.proximo_contato}
                    onChange={(e) => setFormData({...formData, proximo_contato: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Informações adicionais sobre o lead..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => {
                    setShowForm(false)
                    setEditingLead(null)
                    resetForm()
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-save"
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : (editingLead ? 'Atualizar' : 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: nova coluna Kanban (administrador / administrativo) */}
      {podeGerenciarColunasKanban && showNovaColunaModal && (
        <div className="modal-overlay" onClick={() => setShowNovaColunaModal(false)}>
          <div className="modal-content leads-modal-nova-coluna" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nova coluna do Kanban</h3>
              <button type="button" className="btn-close" onClick={() => setShowNovaColunaModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCriarColunaKanban} className="lead-form">
              <p className="leads-modal-nova-coluna-hint">
                O nome da coluna gera o <strong>status</strong> salvo na tabela <code>leads</code>
                (ex.: &quot;Tentativa 1&quot; → <code>tentativa_1</code>).
              </p>
              <div className="form-group">
                <label>Nome da coluna *</label>
                <input
                  type="text"
                  value={novaColunaForm.titulo}
                  onChange={(e) => setNovaColunaForm((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex.: Tentativa 1"
                  required
                />
                {novaColunaForm.titulo.trim() && (
                  <small className="status-preview">
                    Status: <code>{tituloParaStatusId(novaColunaForm.titulo)}</code>
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Descrição (opcional)</label>
                <input
                  type="text"
                  value={novaColunaForm.descricao}
                  onChange={(e) => setNovaColunaForm((f) => ({ ...f, descricao: e.target.value }))}
                  placeholder="Texto exibido abaixo do título da coluna"
                />
              </div>
              <div className="form-group">
                <label>Emoji (opcional)</label>
                <input
                  type="text"
                  maxLength={4}
                  value={novaColunaForm.emoji}
                  onChange={(e) => setNovaColunaForm((f) => ({ ...f, emoji: e.target.value || '📌' }))}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowNovaColunaModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-save">Criar coluna</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: colunas ocultas (administrador / administrativo) */}
      {podeGerenciarColunasKanban && showColunasOcultasModal && (
        <div className="modal-overlay" onClick={() => setShowColunasOcultasModal(false)}>
          <div className="modal-content leads-modal-colunas-ocultas" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Colunas ocultas</h3>
              <button type="button" className="btn-close" onClick={() => setShowColunasOcultasModal(false)}>✕</button>
            </div>
            <div className="leads-colunas-ocultas-list">
              {colunasOcultasLista.length === 0 ? (
                <p>Nenhuma coluna oculta.</p>
              ) : (
                colunasOcultasLista.map((col) => (
                  <div key={col.id} className="leads-coluna-oculta-item">
                    <span>{col.emoji} {col.titulo} <code>({col.id})</code></span>
                    <button type="button" className="btn-restaurar-coluna" onClick={() => restaurarColunaKanban(col.id)}>
                      Exibir
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'warning' })}
      />

      {/* Modal de Movimento */}
      {showMoveModal && leadToMove && (
        <div className="modal-overlay">
          <div className="modal-content move-modal">
            <div className="modal-header">
              <h3>Mover Lead</h3>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowMoveModal(false)
                  setLeadToMove(null)
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="move-modal-content">
              <div className="lead-info-modal">
                <h4>{leadToMove.nome}</h4>
                <p>Status atual: <strong>{findColunaPorId(colunasTodas, leadToMove.status).titulo}</strong></p>
              </div>
              
              <div className="move-options-grid">
                <h5>Escolha o novo status:</h5>
                <div className="options-grid">
                  {colunas.filter(col => col.id !== leadToMove.status).map(coluna => (
                    <button
                      key={coluna.id}
                      className="move-option-card"
                      onClick={() => handleMoveLead(leadToMove, coluna.id)}
                      style={{ 
                        '--option-color': coluna.cor,
                        '--option-bg': coluna.corFundo 
                      }}
                    >
                      <div className="option-header">
                        <span className="option-emoji">{coluna.emoji}</span>
                        <h4>{coluna.titulo}</h4>
                      </div>
                      <p className="option-description">{coluna.descricao}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Histórico */}
      {showHistoryModal && selectedLeadForHistory && (
        <div className="modal-overlay">
          <div className="modal-content history-modal">
            <div className="modal-header">
              <h3>Histórico de Movimentações</h3>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowHistoryModal(false)
                  setSelectedLeadForHistory(null)
                  setLeadHistory([])
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="history-modal-content">
              <div className="lead-info-modal">
                <h4>{selectedLeadForHistory.nome}</h4>
                <p>Status atual: <strong>{findColunaPorId(colunasTodas, selectedLeadForHistory.status).titulo}</strong></p>
              </div>
              
              <div className="history-timeline">
                <h5>Linha do Tempo</h5>
                {leadHistory.length > 0 ? (
                  <div className="timeline">
                    {leadHistory.map((item, index) => (
                      <div key={item.id} className="timeline-item">
                        <div className="timeline-marker">
                          <span className="marker-icon">
                            {findColunaPorId(colunasTodas, item.status_novo).emoji || '📋'}
                          </span>
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <h6>{findColunaPorId(colunasTodas, item.status_anterior).titulo} → {findColunaPorId(colunasTodas, item.status_novo).titulo}</h6>
                            <span className="timeline-date">{formatDate(item.data_movimentacao)}</span>
                          </div>
                          <p className="timeline-description">{item.observacoes}</p>
                          <div className="timeline-user">
                            <span className="user-info">👤 {item.usuarios?.nome || 'Usuário não identificado'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-history">
                    <p>Nenhuma movimentação registrada para este lead.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mensagem */}
      {messageModal.show && (
        <div className="modal-overlay">
          <div className="message-modal">
            <div className={`message-content ${messageModal.type}`}>
              <h4>{messageModal.title}</h4>
              <p>{messageModal.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Leads
